import React, { useState, useEffect } from "react";
import { PLAN_QUOTAS, PLAN_LABELS } from "../lib/plans";
import type { ApiClient } from "@pgs-core/portal-lib/api";
import type { AdminClient } from "../lib/outlet-context";

function QuotaBar({ label, used, limit, unit = "", pending = false }: {
  label: string;
  used: number | null | undefined;
  limit: number | null | undefined;
  unit?: string;
  pending?: boolean;
}) {
  const unlimited = limit == null;
  const usedNum = typeof used === "number" ? used : 0;
  const pct = unlimited || !limit ? 0 : Math.min(100, (usedNum / limit) * 100);
  const over = !unlimited && limit != null && usedNum > limit;
  const usedText = pending ? "…" : typeof used === "number" ? fmt(used) : "—";
  const suffix = unit ? ` ${unit}` : "";

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">
          {usedText}
          {unlimited ? (
            <span className="text-muted-foreground"> {suffix} · Unlimited</span>
          ) : (
            <span className="text-muted-foreground">
              {" "}/ {fmt(limit)}
              {suffix}
            </span>
          )}
        </span>
      </div>
      {!unlimited && (
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full rounded-full ${over ? "bg-destructive" : "bg-primary"}`}
            style={{ width: `${Math.max(pct, usedNum > 0 ? 3 : 0)}%` }}
          />
        </div>
      )}
    </div>
  );
}

function fmt(n: number | null | undefined): string {
  if (typeof n !== "number") return "—";
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// Quota usage for the client's plan. Portal-user count comes from the admin
// client row; storage usage is fetched from /tenant/status (act-as) once the
// site is provisioned. Limits come from the shared PLAN_QUOTAS table.
export function QuotasPanel({ client, api }: { client: AdminClient; api: ApiClient | null }) {
  const quota = (client.plan && PLAN_QUOTAS[client.plan]) || null;
  const [storageUsedGb, setStorageUsedGb] = useState<number | null>(null);

  const provisioned = Boolean(client.d1_database_id);

  useEffect(() => {
    if (!api || !provisioned) return;
    let cancelled = false;
    api.request<{ storage_used_gb?: number | null }>("/tenant/status", {
      headers: { "x-act-as-tenant": client.id },
    })
      .then((status) => {
        if (!cancelled) setStorageUsedGb(status?.storage_used_gb ?? null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api, client.id, provisioned]);

  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Quotas
      </p>
      {!quota ? (
        <p className="text-sm italic text-muted-foreground">
          No quotas defined for the {(client.plan && PLAN_LABELS[client.plan]) || client.plan} plan.
        </p>
      ) : (
        <div className="space-y-4">
          <QuotaBar label="Portal users" used={client.user_count ?? 0} limit={quota.users} />
          <QuotaBar
            label="Storage"
            used={provisioned ? storageUsedGb : 0}
            limit={quota.storageGb}
            unit="GB"
            pending={provisioned && storageUsedGb === null}
          />
        </div>
      )}
    </div>
  );
}
